import { EAddressType } from "@/widgets/quote-new/business-widget/address-widget/state";
import {
  CustomerAddressSubFieldKey,
  CustomerMainRequiredKey,
  effectiveAddressSubFieldRequired,
  getUsCountryDropdownLabelKey,
  isUsAddressRegionRequired,
  type CustomerRequiredFieldRules,
} from "@/utils/customer-required-fields";

const addressInputs1 = (
  t,
  state: any,
  isUs: boolean,
  countriesWithCodes: any[],
  usStates: any[],
  rules: CustomerRequiredFieldRules,
) => {
  const addressMain = rules.main.has(CustomerMainRequiredKey.ADDRESS);
  const reqAddr = (k: string) =>
    effectiveAddressSubFieldRequired(rules, addressMain, k);
  const countryLabelKey = getUsCountryDropdownLabelKey(rules);
  const commonFields = [
    {
      name: "addressType",
      label: "sales.cart.addressType",
      type: "select",
      placeholder: "sales.cart.addressType",
      required: false,
      parameterKey: "type",
      options: [
        {
          value: EAddressType.DELIVERY,
          text: `${t("sales.cart.deliveryAddress")}`,
        },
        {
          value: EAddressType.BILLING,
          text: `${t("sales.cart.billingAddress")}`,
        },
      ],
      value: state?.type,
    },
  ];

  const defaultSwitch = {
    name: "default",
    label: "customers.modal.default",
    type: "switch",
    placeholder: "customers.modal.default",
    required: false,
    parameterKey: "isDefault",
    options: [],
    value: !!state?.isDefault,
    isValid: true,
  };

  const usFields = [
    {
      name: "country",
      label: countryLabelKey,
      type: "select",
      placeholder: countryLabelKey,
      required: isUs ? isUsAddressRegionRequired(rules) : false,
      parameterKey: "country",
      options: countriesWithCodes,
      value: state?.country,
    },
    {
      name: "address2",
      label: "customers.modal.address2",
      type: "text",
      placeholder: "customers.modal.address2",
      required: reqAddr(CustomerAddressSubFieldKey.STREET),
      parameterKey: "address2",
      options: [],
      value: state?.address2,
    },
    {
      name: "address3",
      label: "customers.modal.address3",
      type: "text",
      placeholder: "customers.modal.address3",
      required: reqAddr(CustomerAddressSubFieldKey.HOME),
      parameterKey: "address3",
      options: [],
      value: state?.address3,
    },
    {
      name: "city",
      label: "sales.quote.city",
      type: "text",
      placeholder: "sales.quote.city",
      required: reqAddr(CustomerAddressSubFieldKey.CITY),
      parameterKey: "city",
      options: [],
      value: state?.city,
    },
    {
      name: "state",
      label: "customers.modal.state",
      type: "select",
      placeholder: "customers.modal.state",
      required: reqAddr(CustomerAddressSubFieldKey.STATE),
      parameterKey: "state",
      options: usStates.map(({ name }) => ({ text: name, value: name })),
      value: state?.state,
    },
    {
      name: "zipCode",
      label: "customers.modal.zipCode",
      type: "text",
      placeholder: "customers.modal.zipCode",
      required: reqAddr(CustomerAddressSubFieldKey.ZIP_CODE),
      parameterKey: "zipCode",
      options: [],
      value: state?.zipCode,
    },
  ].filter(Boolean); // removes false (non-US state field if not US)

  const standardFields = [
    {
      name: "city",
      label: "customers.modal.city",
      type: "text",
      placeholder: "customers.modal.city",
      required: reqAddr(CustomerAddressSubFieldKey.CITY),
      parameterKey: "city",
      options: [],
      value: state?.city,
    },
    {
      name: "street",
      label: "customers.modal.street",
      type: "text",
      placeholder: "customers.modal.street",
      required: reqAddr(CustomerAddressSubFieldKey.STREET),
      parameterKey: "street",
      options: [],
      value: state?.street,
      isValid: true,
    },
    {
      name: "home",
      label: "customers.modal.home",
      type: "text",
      placeholder: "customers.modal.home",
      required: reqAddr(CustomerAddressSubFieldKey.HOME),
      parameterKey: "homeNumber",
      options: [],
      value: state?.homeNumber,
      isValid: true,
    },
    {
      name: "entrance",
      label: "customers.modal.entrance",
      type: "text",
      placeholder: "customers.modal.entrance",
      required: reqAddr(CustomerAddressSubFieldKey.ENTRANCE),
      parameterKey: "entry",
      options: [],
      value: state?.entry,
      isValid: true,
    },
    {
      name: "floor",
      label: "customers.modal.floor",
      type: "text",
      placeholder: "customers.modal.floor",
      required: reqAddr(CustomerAddressSubFieldKey.FLOOR),
      parameterKey: "floor",
      options: [],
      value: state?.floor,
      isValid: true,
    },
    {
      name: "apartment",
      label: "customers.modal.apartment",
      type: "text",
      placeholder: "customers.modal.apartment",
      required: reqAddr(CustomerAddressSubFieldKey.APARTMENT),
      parameterKey: "apartment",
      options: [],
      value: state?.apartment,
      isValid: true,
    },
    {
      name: "zipCode",
      label: "customers.modal.zipCode",
      type: "text",
      placeholder: "customers.modal.zipCode",
      required: reqAddr(CustomerAddressSubFieldKey.ZIP_CODE),
      parameterKey: "zipCode",
      options: [],
      value: state?.zipCode,
      isValid: true,
    },
    {
      name: "postbox",
      label: "customers.modal.po",
      type: "text",
      placeholder: "customers.modal.po",
      required: reqAddr(CustomerAddressSubFieldKey.PO_BOX),
      parameterKey: "postbox",
      options: [],
      value: state?.postbox,
      isValid: true,
    },
    {
      name: "remarks",
      label: "customers.modal.remarks",
      type: "text",
      placeholder: "customers.modal.remarks",
      required: reqAddr(CustomerAddressSubFieldKey.REMARKS),
      parameterKey: "notes",
      options: [],
      value: state?.notes,
      isValid: true,
    },
  ];

  return [
    ...commonFields,
    ...(isUs ? usFields : standardFields),
    defaultSwitch,
  ];
};

export { addressInputs1 };
